/*
=========================================================
* RingOperator Personalization — "Pick a voice" dark panel
=========================================================
*/

import { useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, brandAccent, brandCream } from "pages/RingOperatorHomepage/theme";
import Waveform from "pages/RingOperatorHomepage/sections/Waveform";

const voices = [
  { name: "Maya", tone: "Warm · American" },
  { name: "Atlas", tone: "Crisp · British" },
  { name: "Nia", tone: "Calm · Neutral" },
  { name: "Rio", tone: "Upbeat · Spanish" },
];

function Personalization() {
  const [active, setActive] = useState(0);

  return (
    <MKBox component="section" className="ro-root" sx={{ py: { xs: 8, md: 12 }, background: brandCream }}>
      <Container>
        <MKBox
          sx={{
            background: brandPrimary,
            color: brandCream,
            borderRadius: 4,
            p: { xs: 4, md: 7 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <MKBox
                className="ro-mono"
                sx={{
                  fontSize: 12,
                  color: brandAccent,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  mb: 2.5,
                }}
              >
                ◦ Voices
              </MKBox>
              <MKTypography
                component="h2"
                sx={{
                  m: 0,
                  fontSize: { xs: "2.4rem", md: "4.5rem" },
                  lineHeight: 0.95,
                  letterSpacing: "-0.03em",
                  fontWeight: 800,
                  color: brandCream,
                }}
              >
                Pick a{" "}
                <MKBox
                  component="span"
                  className="ro-serif"
                  sx={{ fontStyle: "italic", color: brandAccent }}
                >
                  voice.
                </MKBox>
              </MKTypography>
              <MKTypography
                sx={{ color: "rgba(244,241,234,0.65)", fontSize: "1rem", mt: 2.5, maxWidth: 360, lineHeight: 1.55 }}
              >
                Ten studio-quality neural voices. Tap one to hear the waveform — the personality your customers get when they call.
              </MKTypography>
              <MKBox sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 1 }}>
                {voices.map((v, i) => (
                  <MKBox
                    key={i}
                    component="button"
                    onClick={() => setActive(i)}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: "14px 18px",
                      borderRadius: 2,
                      background: active === i ? brandAccent : "transparent",
                      color: active === i ? "#fff" : brandCream,
                      border: active === i ? "none" : "1px solid rgba(244,241,234,0.15)",
                      fontSize: "0.95rem",
                      fontWeight: active === i ? 600 : 500,
                      transition: "all 180ms ease",
                    }}
                  >
                    <MKBox component="span" sx={{ display: "inline-flex", gap: 1.5, alignItems: "center" }}>
                      <MKBox
                        component="span"
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: active === i ? "#fff" : "rgba(244,241,234,0.1)",
                          color: active === i ? brandAccent : brandCream,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                        }}
                      >
                        ▶
                      </MKBox>
                      {v.name}
                    </MKBox>
                    <MKBox
                      component="span"
                      className="ro-mono"
                      sx={{ fontSize: 12, opacity: 0.75 }}
                    >
                      {v.tone}
                    </MKBox>
                  </MKBox>
                ))}
              </MKBox>
            </Grid>

            <Grid item xs={12} md={7}>
              <MKBox
                sx={{
                  background: "rgba(244,241,234,0.04)",
                  border: "1px solid rgba(244,241,234,0.12)",
                  borderRadius: 3,
                  p: { xs: 3, md: 5 },
                  minHeight: { xs: 280, md: 360 },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <MKBox
                  className="ro-mono"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "rgba(244,241,234,0.55)",
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  <span>now playing</span>
                  <MKBox component="span" sx={{ color: brandAccent }}>● live</MKBox>
                </MKBox>
                <MKBox
                  sx={{
                    flex: 1,
                    color: brandAccent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                  }}
                >
                  <MKBox sx={{ width: "100%", height: { xs: 120, md: 180 } }}>
                    <Waveform key={active} bars={60} height={180} speed={0.9 + active * 0.25} variance={0.9 + active * 0.1} />
                  </MKBox>
                </MKBox>
                <MKBox>
                  <MKTypography
                    sx={{
                      fontSize: { xs: "2rem", md: "2.25rem" },
                      fontWeight: 800,
                      letterSpacing: "-0.02em",
                      color: brandCream,
                    }}
                  >
                    {voices[active].name}
                  </MKTypography>
                  <MKTypography
                    className="ro-mono"
                    sx={{ fontSize: 13, color: "rgba(244,241,234,0.55)", mt: 0.5 }}
                  >
                    {voices[active].tone}
                  </MKTypography>
                </MKBox>
              </MKBox>
            </Grid>
          </Grid>
        </MKBox>
      </Container>
    </MKBox>
  );
}

export default Personalization;
